import { useState, useMemo, useCallback } from 'react';
import { format, getDaysInMonth, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, Save, Send, Trash2, Plus, Layers, Palmtree, Star, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import {
  useMonthlyRoster,
  useShiftCodes,
  useSaveScheduledShifts,
  usePublishRoster,
  useDeleteScheduledShifts,
  useDeleteIndividualShifts,
  useSeedDefaultShiftCodes,
} from '@/hooks/schedule';
import { useCompanyLeaveOverlay } from '@/hooks/leave';
import { usePublicHolidayOverlay } from '@/hooks/usePublicHolidayOverlay';
import type { ScheduledShiftInput, IndividualShiftDeletion } from '@/services/schedule/rosterService';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TemplateManager } from '@/components/roster/TemplateManager';
import { ApplyTemplateDialog } from '@/components/roster/ApplyTemplateDialog';

const RosterPlanner = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pendingChanges, setPendingChanges] = useState<Map<string, ScheduledShiftInput>>(new Map());
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showApplyTemplateDialog, setShowApplyTemplateDialog] = useState(false);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth() + 1;
  const daysInMonth = getDaysInMonth(currentMonth);

  // Queries
  const { data: rosterData, isLoading: isLoadingRoster } = useMonthlyRoster(
    profile?.company_id,
    year,
    month
  );
  
  const { data: shiftCodes, isLoading: isLoadingCodes } = useShiftCodes(profile?.company_id);

  // Leave overlay query for the month
  const monthStart = useMemo(() => format(startOfMonth(currentMonth), 'yyyy-MM-dd'), [currentMonth]);
  const monthEnd = useMemo(() => format(endOfMonth(currentMonth), 'yyyy-MM-dd'), [currentMonth]);
  const { data: leaveOverlays = [] } = useCompanyLeaveOverlay(profile?.company_id, monthStart, monthEnd);
  
  // Public holiday overlays for the month
  const { holidays: publicHolidays } = usePublicHolidayOverlay(monthStart, monthEnd);
  
  // Build a Set for O(1) holiday date lookups in the render loop
  const holidayMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const h of publicHolidays) {
      map.set(h.date, h.name);
    }
    return map;
  }, [publicHolidays]);

  // Helper to check if an employee is on leave for a specific date
  const getLeaveForEmployeeDate = useCallback((profileId: string, date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return leaveOverlays.find(leave => 
      leave.profile_id === profileId &&
      leave.start_date <= dateStr &&
      leave.end_date >= dateStr
    );
  }, [leaveOverlays]);

  // Mutations
  const { mutate: saveShifts, isPending: isSaving } = useSaveScheduledShifts();
  const { mutate: publishRoster, isPending: isPublishing } = usePublishRoster();
  const { mutate: deleteShifts, isPending: isDeleting } = useDeleteScheduledShifts();
  const { mutate: deleteIndividual, isPending: isDeletingIndividual } = useDeleteIndividualShifts();
  const { mutate: seedCodes, isPending: isSeeding } = useSeedDefaultShiftCodes();

  // Generate array of dates for the month
  const dates = useMemo(() => {
    const start = startOfMonth(currentMonth);
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(start);
      date.setDate(i + 1);
      return date;
    });
  }, [currentMonth, daysInMonth]);

  // Navigation handlers
  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToCurrentMonth = () => setCurrentMonth(new Date());

  // Handle shift code change
  const handleShiftCodeChange = useCallback((
    profileId: string,
    date: Date,
    shiftCode: string | null
  ) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const key = `${profileId}-${dateStr}`;

    if (shiftCode === null) {
      // User wants to clear the shift
      // Check if there's an existing shift in DB
      const employee = rosterData?.find(e => e.profile_id === profileId);
      const existingShift = employee?.days[dateStr];
      
      if (existingShift && existingShift.shift_code) {
        // Add to deletions
        setPendingDeletions(prev => new Set(prev).add(key));
      }
      
      // Remove from pendingChanges if it was pending
      setPendingChanges(prev => {
        const updated = new Map(prev);
        updated.delete(key);
        return updated;
      });
    } else {
      // Normal shift selection - remove from deletions if present
      setPendingDeletions(prev => {
        const updated = new Set(prev);
        updated.delete(key);
        return updated;
      });
      
      // Find the full shift code object to get split_segments
      const shiftCodeObj = shiftCodes?.find(sc => sc.code === shiftCode);
      
      // Add to pendingChanges
      setPendingChanges(prev => {
        const updated = new Map(prev);
        updated.set(key, {
          profile_id: profileId,
          date: dateStr,
          shift_code: shiftCode,
          note: null,
          split_segments: shiftCodeObj?.split_segments || null,
        });
        return updated;
      });
    }
  }, [rosterData, shiftCodes]);

  // Pre-index shift codes for O(1) lookup in render loop
  const shiftCodeMap = useMemo(() => {
    const map = new Map<string, (typeof shiftCodes extends (infer T)[] | undefined ? T : never)>();
    shiftCodes?.forEach(c => map.set(c.code, c));
    return map;
  }, [shiftCodes]);

  // Get shift code for a cell (check pending changes first, then roster data)
  // Accepts the employee object directly to avoid redundant rosterData.find() scans
  const getCellShiftCode = useCallback((employee: { days: Record<string, { shift_code: string }> }, profileId: string, date: Date): string | null => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const key = `${profileId}-${dateStr}`;
    
    // Check if marked for deletion first
    if (pendingDeletions.has(key)) {
      return null;
    }
    
    // Check pending changes
    const pending = pendingChanges.get(key);
    if (pending) return pending.shift_code;
    
    // Direct property access on employee object - O(1)
    return employee?.days?.[dateStr]?.shift_code || null;
  }, [pendingChanges, pendingDeletions]);

  // Save handler
  const handleSave = () => {
    if (pendingChanges.size === 0 && pendingDeletions.size === 0) {
      toast({
        title: 'No changes',
        description: 'Make some changes to the roster before saving',
      });
      return;
    }

    let deletionPromise = Promise.resolve();
    let savePromise = Promise.resolve();

    // Process deletions first
    if (pendingDeletions.size > 0) {
      const deletionEntries: IndividualShiftDeletion[] = Array.from(pendingDeletions).map(key => {
        // Key format is: "uuid-YYYY-MM-DD"
        // Extract last 10 chars as date (YYYY-MM-DD), rest is UUID
        const dateStr = key.slice(-10);
        const profileId = key.slice(0, -11); // Remove the date and the dash
        return {
          profile_id: profileId,
          date: dateStr,
          year,
          month,
        };
      });

      // Debug logging to verify no duplicates
      console.log('Deletion entries being sent:', deletionEntries);
      console.log('Unique deletion keys:', new Set(deletionEntries.map(e => `${e.profile_id}-${e.date}`)).size, 'of', deletionEntries.length);
      
      deletionPromise = new Promise<void>((resolve, reject) => {
        deleteIndividual(deletionEntries, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    }

    // Process additions/modifications
    if (pendingChanges.size > 0) {
      const shiftsToSave = Array.from(pendingChanges.values());
      savePromise = new Promise<void>((resolve, reject) => {
        saveShifts(shiftsToSave, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    }

    // Wait for both to complete
    Promise.all([deletionPromise, savePromise])
      .then(() => {
        setPendingChanges(new Map());
        setPendingDeletions(new Set());
      })
      .catch((error) => {
        console.error('Error saving roster:', error);
        toast({
          title: 'Save failed',
          description: 'Some changes could not be saved. Your pending changes have been preserved — please try again.',
          variant: 'destructive',
        });
      });
  };

  // Publish handler
  const handlePublish = () => {
    setShowPublishDialog(true);
  };

  const confirmPublish = () => {
    publishRoster({ year, month }, {
      onSuccess: () => {
        setShowPublishDialog(false);
        setPendingChanges(new Map());
      },
    });
  };

  // Clear handler
  const handleClearMonth = () => {
    setShowClearDialog(true);
  };

  const confirmClear = () => {
    deleteShifts({ year, month }, {
      onSuccess: () => {
        setShowClearDialog(false);
        setPendingChanges(new Map());
        setPendingDeletions(new Set());
      },
    });
  };

  // Seed shift codes
  const handleSeedShiftCodes = () => {
    seedCodes();
  };

  const isLoading = isLoadingRoster || isLoadingCodes;
  const hasChanges = pendingChanges.size > 0 || pendingDeletions.size > 0;

  return (
    <div className="container mx-auto p-4 space-y-4">
      <Tabs defaultValue="roster" className="space-y-4">
        {/* Header with Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold">Roster Planner</h1>
            </div>
            <TabsList>
              <TabsTrigger value="roster">Monthly Roster</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* Roster Tab Content */}
        <TabsContent value="roster" className="space-y-4">
          {/* Action Buttons */}
          <div className="flex items-center justify-start gap-2">
            {shiftCodes?.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSeedShiftCodes}
                disabled={isSeeding}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Default Shift Codes
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowApplyTemplateDialog(true)}
            >
              <Layers className="h-4 w-4 mr-2" />
              Apply Template
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearMonth}
              disabled={isDeleting || isLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Month
            </Button>


            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/business-settings/shift-codes')}
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Shift Codes
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving || isDeletingIndividual}
            >
              <Save className="h-4 w-4 mr-2" />
              Save {hasChanges && `(${pendingChanges.size + pendingDeletions.size})`}
            </Button>
            
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={isPublishing || hasChanges}
            >
              <Send className="h-4 w-4 mr-2" />
              Publish to Schedule
            </Button>
          </div>

      {/* Month Navigation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousMonth}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <CardTitle className="text-2xl">
              {format(currentMonth, 'MMMM yyyy')}
            </CardTitle>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={goToCurrentMonth}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={goToNextMonth}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : shiftCodes?.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No shift codes defined yet. Create default shift codes to get started.
              </p>
              <Button onClick={handleSeedShiftCodes} disabled={isSeeding}>
                <Plus className="h-4 w-4 mr-2" />
                Create Default Shift Codes
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-[5] bg-background border p-2 text-left min-w-[150px]">
                      Employee
                    </th>
                    {dates.map(date => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const isHoliday = holidayMap.has(dateStr);
                      return (
                        <th key={date.toISOString()} className={`border p-1 text-center min-w-[80px] ${isHoliday ? 'bg-rose-50/60 dark:bg-rose-950/20' : ''}`}>
                          <div className="text-xs text-muted-foreground">
                            {format(date, 'EEE')}
                          </div>
                          <div className="font-semibold">
                            {format(date, 'd')}
                          </div>
                          {isHoliday && (
                            <div className="flex items-center justify-center gap-0.5 mt-0.5">
                              <Star className="h-2.5 w-2.5 text-rose-500 fill-current" />
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Public Holiday Row */}
                  {publicHolidays.length > 0 && (
                    <tr className="bg-rose-50/50 dark:bg-rose-950/10">
                      <td className="sticky left-0 z-[5] bg-rose-50/50 dark:bg-rose-950/10 border p-2">
                        <div className="flex items-center gap-1 text-rose-700 dark:text-rose-300 font-medium text-sm">
                          <Star className="h-4 w-4 fill-current" />
                          <span>Holidays</span>
                        </div>
                      </td>
                      {dates.map(date => {
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const holidayName = holidayMap.get(dateStr);
                        return (
                          <td key={`holiday-${date.toISOString()}`} className={`border p-1 align-top ${holidayName ? 'bg-rose-50/30 dark:bg-rose-950/10' : ''}`}>
                            {holidayName && (
                              <div className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border border-rose-200 dark:border-rose-700 truncate text-center" title={holidayName}>
                                {holidayName}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {/* Leave Overlay Row - "All Day" equivalent */}
                  {leaveOverlays.length > 0 && (
                    <tr className="bg-amber-50/50 dark:bg-amber-900/10">
                      <td className="sticky left-0 z-[5] bg-amber-50/50 dark:bg-amber-900/10 border p-2">
                        <div className="flex items-center gap-1 text-amber-700 dark:text-amber-300 font-medium text-sm">
                          <Palmtree className="h-4 w-4" />
                          <span>On Leave</span>
                        </div>
                      </td>
                      {dates.map(date => {
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const leavesOnDate = leaveOverlays.filter(leave => 
                          leave.start_date <= dateStr && leave.end_date >= dateStr
                        );
                        
                        return (
                          <td key={`leave-${date.toISOString()}`} className="border p-1 align-top">
                            <div className="flex flex-col gap-0.5">
                              {leavesOnDate.map(leave => (
                                <button
                                  key={leave.leave_request_id}
                                  onClick={() => navigate(`/manager/leave?request=${leave.leave_request_id}`)}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-colors truncate text-left"
                                  title={`${leave.profile_name} - ${leave.leave_type}`}
                                >
                                  {leave.profile_name.split(' ')[0]}
                                </button>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {rosterData?.map(employee => (
                    <tr key={employee.profile_id}>
                      <td className="sticky left-0 z-[5] bg-background border p-2">
                        <div className="font-medium">{employee.employee_name}</div>
                        <div className="text-xs text-muted-foreground">{employee.role}</div>
                      </td>
                       {dates.map(date => {
                        const currentCode = getCellShiftCode(employee, employee.profile_id, date);
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const key = `${employee.profile_id}-${dateStr}`;
                        const isPending = pendingChanges.has(key) || pendingDeletions.has(key);
                        const shiftCode = currentCode ? shiftCodeMap.get(currentCode) : undefined;
                        
                        return (
                          <td key={date.toISOString()} className="border p-1">
                            <Select
                              value={currentCode || 'empty'}
                              onValueChange={(value) => 
                                handleShiftCodeChange(
                                  employee.profile_id, 
                                  date, 
                                  value === 'empty' ? null : value
                                )
                              }
                            >
                              <SelectTrigger 
                                className={`h-8 text-xs ${isPending ? 'border-primary' : ''} ${
                                  currentCode === 'MANUAL' ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/20' : ''
                                }`}
                              >
                                {currentCode ? (
                                  <div className="flex items-center gap-1.5">
                                    <div
                                      className="w-2.5 h-2.5 rounded flex-shrink-0"
                                      style={{ 
                                        backgroundColor: shiftCode?.color 
                                      }}
                                    />
                                    <span>{currentCode}</span>
                                    {currentCode === 'MANUAL' && (
                                      <Badge variant="outline" className="text-[10px] px-1 py-0 ml-0.5 bg-purple-100 dark:bg-purple-900 border-purple-300 dark:border-purple-700">
                                        Custom
                                      </Badge>
                                    )}
                                    {shiftCode?.is_split_shift && (
                                      <Badge variant="outline" className="text-[10px] px-1 py-0 ml-0.5">
                                        {shiftCode.split_segments?.length || 0}
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <span>-</span>
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="empty">-</SelectItem>
                                {shiftCodes?.map(code => (
                                  <SelectItem key={code.id} value={code.code}>
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-3 h-3 rounded"
                                        style={{ backgroundColor: code.color }}
                                      />
                                      <span>{code.code} - {code.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shift Code Legend */}
      {shiftCodes && shiftCodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Shift Code Legend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {shiftCodes.map(code => (
                <div key={code.id} className="flex items-center gap-3 p-2 rounded-lg border">
                  <div
                    className="w-4 h-4 rounded flex-shrink-0"
                    style={{ backgroundColor: code.color }}
                  />
                  <div className="flex-shrink-0">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {code.code} - {code.name}
                      {code.code === 'MANUAL' && (
                        <Badge variant="secondary" className="text-[10px]">
                          Created in Schedule Planner
                        </Badge>
                      )}
                    </div>
                    {code.start_time && code.end_time && (
                      <div className="text-xs text-muted-foreground">
                        {code.start_time} - {code.end_time}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Publish Confirmation Dialog */}
      <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Roster to Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will convert all scheduled shifts for {format(currentMonth, 'MMMM yyyy')} into 
              actual shifts in the weekly schedule. This action will create shifts that employees 
              can view and interact with.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPublish} disabled={isPublishing}>
              Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Confirmation Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Shifts?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all scheduled shifts for {format(currentMonth, 'MMMM yyyy')}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmClear} 
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </TabsContent>

        {/* Templates Tab Content */}
        <TabsContent value="templates" className="space-y-4">
          <TemplateManager />
        </TabsContent>
      </Tabs>

      {/* Apply Template Dialog */}
      <ApplyTemplateDialog
        open={showApplyTemplateDialog}
        year={year}
        month={month}
        onClose={() => setShowApplyTemplateDialog(false)}
        onSuccess={() => {
          setPendingChanges(new Map());
          setPendingDeletions(new Set());
        }}
      />
    </div>
  );
};

export default RosterPlanner;
